import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import LoadingSpinner from './LoadingSpinner';
import logoIcon from './assets/icon.png';
import productionsBg from './assets/productions.png';

const Projects = ({ searchQuery, setSearchQuery }) => {
  const [activeTab, setActiveTab] = useState('released');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const openVideoModal = (trailerLink, title) => {
    setSelectedVideo({ trailerLink, title });
    setIsVideoModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
    document.body.style.overflow = 'unset';
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1` : url;
  };

  const fetchProjects = async () => {
    try {
      const moviesQuery = query(collection(db, 'movies'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(moviesQuery);
      const moviesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProjects(moviesList);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects
    .filter(project => project.status === activeTab)
    .filter(project =>
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Auto-scroll effect
  useEffect(() => {
    if (!isExpanded && !isHovering && scrollContainerRef.current && filteredProjects.length > 3) {
      const container = scrollContainerRef.current;
      let scrollAmount = 0;
      const scrollSpeed = 1;
      const scrollInterval = setInterval(() => {
        if (container) {
          scrollAmount += scrollSpeed;
          container.scrollLeft = scrollAmount;
          // Reset scroll when reaching the end
          if (scrollAmount >= container.scrollWidth - container.clientWidth) {
            scrollAmount = 0;
          }
        }
      }, 30);

      return () => clearInterval(scrollInterval);
    }
  }, [isExpanded, isHovering, filteredProjects.length]);

  return (
    <section 
      id="projects" 
      className="projects"
      style={{
        backgroundImage: `url(${productionsBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative'
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(227, 242, 253, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%)' }}></div>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Title on Line 1 */}
        <div className="text-center mb-6">
          <h2 className="section-title projects-title-desktop" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0' }}>
            <img src={logoIcon} alt="O" style={{ width: '3.5rem', height: '3.5rem', objectFit: 'contain', marginRight: '-0.2rem', border: '2px solid #B8860B', borderRadius: '50%', padding: '2px' }} />ur Latest Pr
            <img src={logoIcon} alt="o" style={{ width: '2.8rem', height: '2.8rem', objectFit: 'contain', margin: '0 -0.2rem', border: '2px solid #B8860B', borderRadius: '50%', padding: '2px' }} />ductions
          </h2>
          <h2 className="section-title projects-title-mobile" style={{ display: 'none', alignItems: 'center', justifyContent: 'center', gap: '0' }}>
            <img src={logoIcon} alt="O" style={{ width: '3rem', height: '3rem', objectFit: 'contain', marginRight: '-0.2rem', border: '2px solid #B8860B', borderRadius: '50%', padding: '2px' }} />ur Pr
            <img src={logoIcon} alt="o" style={{ width: '2.5rem', height: '2.5rem', objectFit: 'contain', margin: '0 -0.2rem', border: '2px solid #B8860B', borderRadius: '50%', padding: '2px' }} />ductions
          </h2>
          <p className="section-subtitle">Explore our portfolio of award-winning productions</p>
        </div>

        {/* Tabs and Search on Line 2 */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8 projects-controls">
          {/* Tabs */}
          <div className="inline-flex bg-white rounded-full p-2 shadow-lg border-2" style={{ borderColor: '#B8860B' }}>
            <button
              onClick={() => setActiveTab('released')}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 text-sm`}
              style={activeTab === 'released' 
                ? { background: '#5B9BD5', color: 'white', boxShadow: '0 4px 6px rgba(91, 155, 213, 0.3)', border: '2px solid #B8860B' }
                : { color: '#4A5568', border: '2px solid transparent' }
              }
            >
              Released
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 text-sm`}
              style={activeTab === 'upcoming' 
                ? { background: '#87CEEB', color: 'white', boxShadow: '0 4px 6px rgba(135, 206, 235, 0.3)', border: '2px solid #B8860B' }
                : { color: '#4A5568', border: '2px solid transparent' }
              }
            >
              Upcoming
            </button>
          </div>

          {/* Search Bar */}
          <div className="search-bar projects-search" style={{ margin: 0, width: 'auto', minWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{ fontSize: '0.9rem', padding: '10px 40px 10px 20px' }}
            />
            {/* <span className="search-icon">🔍</span> */}
          </div>
        </div>

        {/* Projects Grid/Scroll */}
        <div 
          ref={scrollContainerRef} 
          className={isExpanded ? "projects-grid" : "projects-scroll"}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {loading ? (
            <div className="col-span-full text-center py-20">
              <LoadingSpinner size="large" message="Loading projects..." />
            </div>
          ) : filteredProjects.length > 0 ? (
            filteredProjects.map((project, index) => (
              <div key={project.id} className="project-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="project-image">
                  <img src={project.imageUrl} alt={project.title} />
                  <div className="project-overlay">
                    {project.trailerLink ? (
                      <button 
                        className="play-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openVideoModal(project.trailerLink, project.title);
                        }}
                      >
                        ▶ Watch {activeTab === 'upcoming' ? 'Trailer' : 'Now'}
                      </button>
                    ) : (
                      <Link to={`/movie/${project.slug}`} className="play-btn">
                        ℹ Know More
                      </Link>
                    )}
                  </div>
                  {activeTab === 'upcoming' && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg z-10">
                      Coming Soon
                    </div>
                  )}
                </div>
                <div className="project-info">
                  <span className="project-category">{project.category}</span>
                  <h3>{project.title}</h3>
                  <p className="project-year">{project.releaseDate || project.year}</p>
                  <Link to={`/movie/${project.slug}`} className="know-more-btn">
                    Know More →
                  </Link>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-20">
              {/* <div className="text-6xl mb-4">🔍</div> */}
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No projects found</h3>
              <p className="text-gray-500">Try adjusting your search terms</p>
            </div>
          )}
        </div>

        {/* View More/Less Button */}
        {filteredProjects.length > 3 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="px-8 py-3 rounded-full font-semibold transition-all duration-300"
              style={{
                background: isExpanded ? '#87CEEB' : '#5B9BD5',
                color: 'white',
                border: '2px solid #B8860B',
                boxShadow: '0 4px 15px rgba(91, 155, 213, 0.4)',
                cursor: 'pointer'
              }}
            >
              {isExpanded ? 'View Less ▲' : 'View More ▼'}
            </button>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {isVideoModalOpen && selectedVideo && (
        <>
          <div className="video-modal-backdrop" onClick={closeVideoModal}></div>
          <div className="video-modal">
            <button className="video-modal-close" onClick={closeVideoModal}>✕</button>
            <div className="video-modal-content">
              <iframe
                src={getYouTubeEmbedUrl(selectedVideo.trailerLink)}
                title={`${selectedVideo.title} Trailer`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default Projects;
